import React, { useCallback, useMemo } from "react";
import clsx from "clsx";
import { useSelector } from "react-redux";
import { useRouteMatch } from "react-router-dom";

import {
	Breadcrumbs,
	Button,
	CollapseSection,
	MainContainer,
	UnderwritingBreadcrumbs,
	UwCollapseSection,
} from "components";
import { LineDivider } from "components/layout";
import { breadcrumbsReportFromMerchantPart } from "helpers";
import {
	getResultTableData,
	LABELS_GIDENTIFY_MATCHED_RECORD,
	LABELS_GIDENTIFY_RESULT_OWNER,
	LABELS_GIDENTIFY_VERIFICATION_RESULT,
} from "mappers";
import {
	useDownloadGidentifyOwnerDocumentMutation,
	useGidentifyAuroraOutcomeFieldMutation,
	useGidentifyOwnerResultQuery,
} from "queries";
import {
	getCurrentUserAccessToken,
	getCurrentUserId,
} from "store/user/user.reducer";
import { UnderwritingTabs, UnderwritingRouteMatchProps } from "types";

import ReportTable from "./reportTable";

import styles from "./reports.module.scss";

const OwnerGIdentifyReport = () => {
	const currentUserId = useSelector(getCurrentUserId);
	const token: string = useSelector(getCurrentUserAccessToken);
	const match: UnderwritingRouteMatchProps = useRouteMatch();

	const isMerchantPage = useMemo(
		() => match?.url?.startsWith("/merchant/"),
		[match?.url],
	);

	const applicationId = useMemo(
		() =>
			isMerchantPage ? match?.params?.applicationId : match?.params?.id,
		[isMerchantPage, match?.params?.applicationId, match?.params?.id],
	);

	const ownerType = match?.params?.ownerType;

	const {
		data: dataOwner,
		isFetching: isLoadingDataOwner,
		refetch: dataOwnerRefetch,
	} = useGidentifyOwnerResultQuery({
		id: applicationId,
		ownerType,
	});

	const { mutate: downloadGidentifyDocument, isLoading: isLoadingDocument } =
		useDownloadGidentifyOwnerDocumentMutation();

	const downloadDocument = () =>
		downloadGidentifyDocument({
			id: applicationId,
			ownerType,
			token: token,
		});

	const {
		mutate: submitGidentifyAuroraOutcome,
		isLoading: isLoadingGidentifyAuroraOutcome,
	} = useGidentifyAuroraOutcomeFieldMutation();

	const gidentifyAuroraOutcomeSubmitHandler = useCallback(
		values => {
			return submitGidentifyAuroraOutcome({
				id: applicationId,
				values,
				ownerType,
				onSuccessHandler: dataOwnerRefetch,
			});
		},
		[
			applicationId,
			dataOwnerRefetch,
			ownerType,
			submitGidentifyAuroraOutcome,
		],
	);

	const isRerun = useMemo(() => !!dataOwner?.rerun, [dataOwner?.rerun]);

	const ownerResultData = getResultTableData(
		dataOwner?.original,
		dataOwner?.rerun,
		LABELS_GIDENTIFY_RESULT_OWNER,
		"potential_matches",
	);

	const verificationResults = getResultTableData(
		dataOwner?.original?.response,
		dataOwner?.rerun?.response,
		LABELS_GIDENTIFY_VERIFICATION_RESULT,
	);

	const firstMatchedRecord = getResultTableData(
		isRerun
			? dataOwner?.rerun?.response?.first_match
			: dataOwner?.original?.response?.first_match,
		isRerun ? dataOwner?.rerun?.response : dataOwner?.rerun,
		LABELS_GIDENTIFY_MATCHED_RECORD,
	);

	const matchesCount = useMemo(
		() =>
			isRerun
				? dataOwner?.rerun?.response?.matches_count
				: dataOwner?.original?.response?.matches_count,
		[
			dataOwner?.original?.response?.matches_count,
			dataOwner?.rerun?.response?.matches_count,
			isRerun,
		],
	);

	const editableFields = useMemo(() => {
		let fields = {};
		if (
			!isMerchantPage &&
			match?.params?.tab !== UnderwritingTabs.Unassigned.name &&
			match?.params?.tab !== UnderwritingTabs.Historical.name &&
			match?.params?.tab !== UnderwritingTabs.QAUnassigned.name &&
			match?.params?.tab !== UnderwritingTabs.QAComplete.name &&
			dataOwner?.underwriter_id &&
			dataOwner?.underwriter_id === currentUserId
		) {
			fields = {
				...fields,
				outcome: {
					submitHandler: gidentifyAuroraOutcomeSubmitHandler,
					isLoading: isLoadingGidentifyAuroraOutcome,
					reactSelectProps: { menuPlacement: "top" },
				},
			};
		}
		return fields;
	}, [
		currentUserId,
		dataOwner?.underwriter_id,
		gidentifyAuroraOutcomeSubmitHandler,
		isLoadingGidentifyAuroraOutcome,
		isMerchantPage,
		match?.params?.tab,
	]);

	return (
		<MainContainer>
			{isMerchantPage ? (
				<Breadcrumbs
					pages={breadcrumbsReportFromMerchantPart({
						id: match.params.id,
						title: "gIdentify",
					})}
				/>
			) : (
				<UnderwritingBreadcrumbs match={match} />
			)}
			<p
				className={clsx(
					styles.tableTitle,
					isLoadingDataOwner && "mb-0",
				)}
			>
				Inquiry
			</p>
			<ReportTable
				data={ownerResultData}
				isRerun={isRerun}
				isLoading={isLoadingDataOwner}
				editableFields={editableFields}
			/>

			<LineDivider />
			<CollapseSection
				header="Verification Results"
				children={
					<ReportTable
						data={verificationResults}
						isRerun={isRerun}
						isLoading={isLoadingDataOwner}
						disableHeader
						alignStart
						isOriginalHidden={isRerun}
					/>
				}
			/>

			<UwCollapseSection
				recordsCount={matchesCount}
				collapseClassName={`${styles.containerCollapse} mt-4`}
				header="Matched Person Data Record"
				children={
					<ReportTable
						data={firstMatchedRecord}
						isRerun={isRerun}
						isLoading={isLoadingDataOwner}
						disableHeader
						alignStart
						isOriginalHidden={isRerun}
					/>
				}
			/>

			<LineDivider />
			<Button
				type="button"
				value="Download"
				onClick={downloadDocument}
				isLoading={isLoadingDataOwner || isLoadingDocument}
			/>
		</MainContainer>
	);
};

export default OwnerGIdentifyReport;
